import { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Cookies from "js-cookie";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import axiosInstance from "../../../axios/axiosInstance";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Chip,
  Divider,
  Stack,
  Link,
  Card,
  CardContent,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";
import LanguageIcon from "@mui/icons-material/Language";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import WorkIcon from "@mui/icons-material/Work";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SchoolIcon from "@mui/icons-material/School";
import StarIcon from "@mui/icons-material/Star";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

function CorporateInterestRequest() {
  const [requests, setRequests] = useState([]);
  const [comments, setComments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [interviewsLoading, setInterviewsLoading] = useState(false);
  const token = Cookies.get("token");

  const [openDialog, setOpenDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedReq, setSelectedReq] = useState(null);

  const handleOpenDialog = (req) => {
    setSelectedReq(req);
    setActiveTab(0);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedReq(null);
  };

  const fetchCorporateRequests = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `/api/profile/candidate/interests`,
      );
      console.log("Fetched requests:", response.data);
      if (response.status === 200) {
        setRequests(response.data);
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
      setError("Failed to load requests. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchInterviews = async () => {
    setInterviewsLoading(true);
    try {
      const response = await axiosInstance.get(
        `/api/profile/interviews/candidate`,
      );
      console.log("Fetched interviews:", response.data);
      if (response.status === 200 && response.data.interviews) {
        setInterviews(response.data.interviews);
      }
    } catch (error) {
      console.error("Error fetching interviews:", error);
    } finally {
      setInterviewsLoading(false);
    }
  };

  const getInterviewForRequest = (request) => {
    if (!request) return null;

    // Check if interview details are directly in the request
    if (request.interview_id) {
      return {
        interviewId: request.interview_id,
        status: request.interview_status || null,
        overallResult: request.interview_overall_result || null,
      };
    }

    // Fallback to matching from interviews array if needed
    if (!interviews || interviews.length === 0) return null;

    return interviews.find((interview) => {
      if (interview.requestType === "interest_based") {
        const corporateId =
          request.interest_context?.corporate?.id || request.corporate_id;
        const corporateName =
          request.corporate_name || request.interest_context?.corporate?.name;

        return (
          (corporateId && interview.corporate?.id === corporateId) ||
          (corporateName && interview.corporate?.companyName === corporateName)
        );
      }
      return false;
    });
  };

  const getInterviewStatusDetails = (request) => {
    const interview = getInterviewForRequest(request);
    if (!interview) return null;
    const interviewDetails = interviews.find(
      (interview) => interview.interviewId === interview.interviewId,
    );
    return {
      ...interviewDetails,
      interviewId: interview.interviewId,
      status: interview.status,
      overallResult: interview.overallResult,
    };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
      case "accepted":
      case "slot_selected":
        return {
          backgroundColor: "#9b42e9",
          color: "#FFFFFF",
        };
      case "cancelled":
      case "slot_rejected":
      case "rejected":
      case "declined":
        return {
          backgroundColor: "#ba68c8",
          color: "#FFFFFF",
        };
      case "pending":
      case "requested":
        return {
          backgroundColor: "#8c5eb4ff",
          color: "#FFFFFF",
        };
      case "scheduled":
      case "in_progress":
      case "slot_proposed":
      case "slots_proposed":
      default:
        return {
          backgroundColor: "#8c5eb4ff",
          color: "#FFFFFF",
        };
    }
  };

  const getResultColor = (result) => {
    if (result === "selected") {
      return {
        backgroundColor: "#9b42e9",
        color: "#FFFFFF",
      };
    }
    if (result === "rejected") {
      return {
        backgroundColor: "#ba68c8",
        color: "#FFFFFF",
      };
    }
    return {
      backgroundColor: "#8c5eb4ff",
      color: "#FFFFFF",
    };
  };

  const handleAction = async (requestId, action) => {
    if (!comments[requestId] || comments[requestId].trim() === "") {
      setError("Message for company is required.");
      return;
    }

    try {
      const response = await axiosInstance.put(
        `/api/profile/candidate/interests/${requestId}`,
        {
          decision: action,
          comment: comments[requestId],
        },
      );

      if (response.status === 200) {
        setRequests((prev) =>
          prev.map((request) =>
            request.id === requestId
              ? { ...request, status: response.data.status }
              : request,
          ),
        );
      }
    } catch (error) {
      console.error("Error approving candidate:", error);
    }
  };

  const handleCommentChange = (requestId, value) => {
    setComments((prev) => ({ ...prev, [requestId]: value }));
    setError(null);
  };

  useEffect(() => {
    fetchCorporateRequests();
    fetchInterviews();
  }, []);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        width: "100%",
        mx: "auto",
        mt: 4,
        backgroundColor: "background.paper",
        borderRadius: 3,
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}
        >
          Corporate Interest Requests
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Review and respond to corporate interest requests from companies.
        </Typography>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          <CircularProgress size={50} />
        </Box>
      ) : requests.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            px: 2,
            backgroundColor: "grey.50",
            borderRadius: 2,
            border: "2px dashed",
            borderColor: "divider",
          }}
        >
          <CorporateFareIcon
            sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
          />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No requests found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Corporate interest requests will appear here when companies show
            interest in your profile.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {requests.map((req) => (
            <Grid item xs={12} key={req.id}>
              <Card
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 3,
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: 4,
                    borderColor: "primary.main",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h5"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          fontWeight: 600,
                          color: "primary.main",
                          mb: 1,
                        }}
                      >
                        <CorporateFareIcon
                          sx={{ mr: 1.5, fontSize: 28, color: "primary.main" }}
                        />
                        {req.corporate_name}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          flexWrap: "wrap",
                          mt: 1.5,
                        }}
                      >
                        <Chip
                          icon={<WorkIcon />}
                          label={req.job_function_name}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                        <Chip
                          label={req.job_role_name}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                      </Box>
                    </Box>
                    {req.status === "admin_approved" && (
                      <Chip
                        label="Pending Response"
                        // color="warning"
                        size="small"
                        sx={{
                          fontWeight: 600,
                          ...getStatusColor("Pending Response"),
                        }}
                      />
                    )}
                    {req.status === "candidate_interested" && (
                      <Chip
                        label="Accepted"
                        size="small"
                        sx={{
                          fontWeight: 600,
                          backgroundColor: "#9b42e9",
                          color: "#FFFFFF",
                        }}
                      />
                    )}
                    {req.status === "candidate_not_interested" && (
                      <Chip
                        label="Declined"
                        size="small"
                        sx={{
                          fontWeight: 600,
                          backgroundColor: "#ba68c8",
                          color: "#FFFFFF",
                        }}
                      />
                    )}
                    {/* {getInterviewForRequest(req) && (
                      <Chip
                        // icon={<EventNoteIcon />}
                        label={
                          getInterviewForRequest(req).overallResult ===
                          "selected"
                            ? "Selected"
                            : getInterviewForRequest(req).status === "completed"
                            ? "Interview Completed"
                            : getInterviewForRequest(req).status
                            ? getInterviewForRequest(req)
                                .status.replace(/_/g, " ")
                                .replace(/\b\w/g, (char) => char.toUpperCase())
                            : "Pending"
                        }
                        size="small"
                        sx={{
                          fontWeight: 600,
                          ml: 1,
                          ...(getInterviewForRequest(req).overallResult ===
                          "selected"
                            ? getResultColor("selected")
                            : getStatusColor(
                                getInterviewForRequest(req).status
                              )),
                        }}
                      />
                    )} */}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {req.status === "admin_approved" ? (
                    <Box>
                      <Button
                        variant="filled"
                        onClick={() => handleOpenDialog(req)}
                        sx={{
                          mb: 2,
                          backgroundColor: "#9b42e9",
                          color: "white",
                          ":hover": {
                            backgroundColor: "#9b42e9",
                            translate: "0 -1px",
                          },
                        }}
                      >
                        View Company & Job Details
                      </Button>

                      <Box sx={{ mt: 3 }}>
                        {/* Company Message */}
                        {req.corporate_comment && (
                          <Alert
                            severity="info"
                            sx={{
                              mb: 3,
                              "& .MuiAlert-message": {
                                width: "100%",
                              },
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ mb: 1, fontWeight: 600 }}
                            >
                              Message from Company
                            </Typography>
                            <Typography variant="body1">
                              {req.corporate_comment}
                            </Typography>
                          </Alert>
                        )}

                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 1.5,
                            fontWeight: 600,
                            color: "text.primary",
                          }}
                        >
                          Your Response
                        </Typography>
                        <TextField
                          label="Message for company"
                          variant="outlined"
                          fullWidth
                          multiline
                          rows={4}
                          value={comments[req.id] || ""}
                          onChange={(e) =>
                            handleCommentChange(req.id, e.target.value)
                          }
                          sx={{ mb: 2 }}
                          helperText="Please provide your thoughts or questions about this opportunity"
                        />

                        {error && (
                          <Alert
                            severity="info"
                            sx={{
                              mb: 2,
                              backgroundColor: "#f3e5f5",
                              color: "#ba68c8",
                              "& .MuiAlert-icon": { color: "#ba68c8" },
                            }}
                          >
                            {error}
                          </Alert>
                        )}

                        <Stack direction="row" spacing={2}>
                          <Button
                            variant="contained"
                            onClick={() => handleAction(req.id, "approve")}
                            sx={{
                              minWidth: 120,
                              fontWeight: 600,
                              textTransform: "none",
                              backgroundColor: "#9b42e9",
                              "&:hover": { backgroundColor: "#8c5eb4ff" },
                            }}
                          >
                            Accept
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() => handleAction(req.id, "reject")}
                            sx={{
                              minWidth: 120,
                              fontWeight: 600,
                              textTransform: "none",
                              backgroundColor: "#ba68c8",
                              "&:hover": { backgroundColor: "#8c5eb4ff" },
                            }}
                          >
                            Decline
                          </Button>
                        </Stack>
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <Button
                        variant="filled"
                        onClick={() => handleOpenDialog(req)}
                        sx={{
                          mb: 2,
                          backgroundColor: "#9b42e9",
                          color: "white",
                          ":hover": {
                            backgroundColor: "#9b42e9",
                            translate: "0 -1px",
                          },
                        }}
                      >
                        View Company & Job Details
                      </Button>
                      <Alert
                        severity="info"
                        sx={{
                          mt: 2,
                          backgroundColor: "#f3e5f5",
                          color: "#8c5eb4ff",
                          "& .MuiAlert-icon": {
                            color:
                              req.status === "candidate_interested"
                                ? "#9b42e9"
                                : "#ba68c8",
                          },
                        }}
                      >
                        {req.status === "candidate_interested"
                          ? "You have accepted this proposal"
                          : "You have declined this proposal"}
                      </Alert>
                      {getInterviewForRequest(req) && (
                        <Alert
                          icon={
                            <EventNoteIcon
                              sx={{ color: "inherit !important" }}
                            />
                          }
                          sx={{
                            mt: 2,
                            ...(getInterviewForRequest(req).overallResult ===
                              "selected"
                              ? getResultColor("selected")
                              : getInterviewForRequest(req).overallResult ===
                                "rejected"
                                ? getResultColor("rejected")
                                : getInterviewForRequest(req).status ===
                                  "slot_selected"
                                  ? getStatusColor("completed")
                                  : ["slot_rejected", "rejected"].includes(
                                    getInterviewForRequest(req).status,
                                  )
                                    ? getResultColor("rejected")
                                    : getStatusColor(
                                      getInterviewForRequest(req).status,
                                    )),
                            "& .MuiAlert-message": {
                              color: "inherit",
                            },
                            "& .MuiAlert-icon": {
                              color: "inherit !important",
                            },
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 0.5, color: "inherit" }}
                          >
                            Interview Status:{" "}
                            {getInterviewForRequest(req).status === "completed"
                              ? "Completed"
                              : getInterviewForRequest(req).status ===
                                "completed"
                                ? "Completed"
                                : getInterviewForRequest(req).status
                                  ? getInterviewForRequest(req)
                                    .status.replace(/_/g, " ")
                                    .replace(/\b\w/g, (char) =>
                                      char.toUpperCase(),
                                    )
                                  : "In Progress"}
                          </Typography>
                          {getInterviewForRequest(req).overallResult && (
                            <Typography
                              variant="body2"
                              sx={{ color: "inherit" }}
                            >
                              Result:{" "}
                              {getInterviewForRequest(req)
                                .overallResult.charAt(0)
                                .toUpperCase() +
                                getInterviewForRequest(req).overallResult.slice(
                                  1,
                                )}
                            </Typography>
                          )}
                        </Alert>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog for Company and Job Description */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 2,
            borderBottom: "2px solid",
            borderColor: "divider",
            backgroundColor: "primary.main",
            color: "primary.contrastText",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {selectedReq ? selectedReq.corporate_name : ""}
          </Typography>
          <Button
            onClick={handleCloseDialog}
            sx={{
              minWidth: "auto",
              padding: 0.5,
              color: "primary.contrastText",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </Button>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 0,
            overflowY: "auto",
            maxHeight: "80vh",
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ccc",
              borderRadius: 8,
            },
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="fullWidth"
            sx={{
              borderBottom: "1px solid",
              borderColor: "divider",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
              },
              "& .Mui-selected": {
                color: "primary.main",
              },
            }}
          >
            <Tab label="Company Description" />
            <Tab label="Job Description" />
            {getInterviewForRequest(selectedReq) && (
              <Tab label="Interview Status" />
            )}
          </Tabs>

          <Box sx={{ p: 3, maxHeight: "65vh", overflowY: "auto" }}>
            {activeTab === 0 && selectedReq?.interest_context?.corporate && (
              <Box>
                <Stack spacing={3}>
                  {/* Company Name */}
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                        fontWeight: 600,
                        color: "primary.main",
                      }}
                    >
                      <BusinessIcon sx={{ mr: 1.5, fontSize: 32 }} />
                      {selectedReq.interest_context.corporate.name}
                    </Typography>
                  </Box>

                  <Divider />

                  {/* Description */}
                  {selectedReq.interest_context.corporate.description && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        backgroundColor: "grey.50",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 1.5, fontWeight: 600, color: "primary.main" }}
                      >
                        About Company
                      </Typography>
                      <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                        {selectedReq.interest_context.corporate.description}
                      </Typography>
                    </Paper>
                  )}

                  {/* Company Details Grid */}
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
                    >
                      Company Details
                    </Typography>
                    <Grid container spacing={2}>
                      {selectedReq.interest_context.corporate.industry && (
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              textTransform: "uppercase",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              letterSpacing: 0.5,
                            }}
                          >
                            Industry
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ mt: 0.5, fontWeight: 500 }}
                          >
                            {selectedReq.interest_context.corporate.industry}
                          </Typography>
                        </Grid>
                      )}

                      {selectedReq.interest_context.corporate.size && (
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              textTransform: "uppercase",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              letterSpacing: 0.5,
                            }}
                          >
                            Company Size
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ mt: 0.5, fontWeight: 500 }}
                          >
                            {selectedReq.interest_context.corporate.size}{" "}
                            employees
                          </Typography>
                        </Grid>
                      )}

                      {selectedReq.interest_context.corporate.url && (
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              textTransform: "uppercase",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              letterSpacing: 0.5,
                            }}
                          >
                            Website
                          </Typography>
                          <Link
                            href={selectedReq.interest_context.corporate.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mt: 0.5,
                              color: "primary.main",
                              fontWeight: 500,
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            <LanguageIcon sx={{ mr: 0.5, fontSize: 18 }} />
                            {selectedReq.interest_context.corporate.url}
                          </Link>
                        </Grid>
                      )}

                      {selectedReq.interest_context.corporate
                        .business_email && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                textTransform: "uppercase",
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                letterSpacing: 0.5,
                              }}
                            >
                              Email
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mt: 0.5,
                                fontWeight: 500,
                              }}
                            >
                              <EmailIcon
                                sx={{
                                  mr: 0.5,
                                  fontSize: 18,
                                  color: "primary.main",
                                }}
                              />
                              {
                                selectedReq.interest_context.corporate
                                  .business_email
                              }
                            </Typography>
                          </Grid>
                        )}

                      {selectedReq.interest_context.corporate.phone_num && (
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              textTransform: "uppercase",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              letterSpacing: 0.5,
                            }}
                          >
                            Phone
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mt: 0.5,
                              fontWeight: 500,
                            }}
                          >
                            <PhoneIcon
                              sx={{
                                mr: 0.5,
                                fontSize: 18,
                                color: "primary.main",
                              }}
                            />
                            {selectedReq.interest_context.corporate.phone_num}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  {/* Company Values */}
                  {/* {selectedReq.interest_context.corporate.company_value && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        backgroundColor: "grey.50",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 1.5, fontWeight: 600, color: "primary.main" }}
                      >
                        Company Values
                      </Typography>
                      <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                        {selectedReq.interest_context.corporate.company_value}
                      </Typography>
                    </Paper>
                  )} */}
                </Stack>
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                {selectedReq?.interest_context?.job_posting &&
                  Object.keys(selectedReq.interest_context.job_posting).length >
                  0 ? (
                  // --- existing job posting UI (paste the whole <Stack> ... </Stack> block here) ---
                  <Stack spacing={3}>
                    {/* Job Title */}
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1,
                          fontWeight: 600,
                          color: "primary.main",
                        }}
                      >
                        <WorkIcon sx={{ mr: 1.5, fontSize: 32 }} />
                        {selectedReq.interest_context.job_posting.role_name}
                      </Typography>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{ ml: 5 }}
                      >
                        {selectedReq.interest_context.job_posting.function_name}
                      </Typography>
                    </Box>

                    <Divider />

                    {/* Corporate Comment */}
                    {selectedReq.corporate_comment && (
                      <Alert
                        severity="info"
                        sx={{
                          "& .MuiAlert-message": {
                            width: "100%",
                          },
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1, fontWeight: 600 }}
                        >
                          Message from Company
                        </Typography>
                        <Typography variant="body1">
                          {selectedReq.corporate_comment}
                        </Typography>
                      </Alert>
                    )}

                    {/* Job Summary */}
                    {selectedReq.interest_context.job_posting.summary && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          backgroundColor: "grey.50",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            mb: 1.5,
                            fontWeight: 600,
                            color: "primary.main",
                          }}
                        >
                          Job Summary
                        </Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                          {selectedReq.interest_context.job_posting.summary}
                        </Typography>
                      </Paper>
                    )}

                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
                      >
                        Job Details
                      </Typography>
                      <Grid container spacing={2}>
                        {selectedReq.interest_context.job_posting
                          .experience_level && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  textTransform: "uppercase",
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  letterSpacing: 0.5,
                                }}
                              >
                                Experience Level
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ mt: 0.5, fontWeight: 500 }}
                              >
                                {
                                  selectedReq.interest_context.job_posting
                                    .experience_level
                                }
                              </Typography>
                            </Grid>
                          )}

                        {selectedReq.interest_context.job_posting.country && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                textTransform: "uppercase",
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                letterSpacing: 0.5,
                              }}
                            >
                              Location
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mt: 0.5,
                                fontWeight: 500,
                              }}
                            >
                              <LocationOnIcon
                                sx={{
                                  mr: 0.5,
                                  fontSize: 18,
                                  color: "primary.main",
                                }}
                              />
                              {selectedReq.interest_context.job_posting.country}
                            </Typography>
                          </Grid>
                        )}

                        {selectedReq.interest_context.job_posting
                          .employment_type &&
                          selectedReq.interest_context.job_posting
                            .employment_type.length > 0 && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  textTransform: "uppercase",
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  letterSpacing: 0.5,
                                }}
                              >
                                Employment Type
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                sx={{ mt: 0.5 }}
                                flexWrap="wrap"
                              >
                                {selectedReq.interest_context.job_posting.employment_type.map(
                                  (type, index) => (
                                    <Chip
                                      key={index}
                                      label={type}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ fontWeight: 500 }}
                                    />
                                  ),
                                )}
                              </Stack>
                            </Grid>
                          )}

                        {selectedReq.interest_context.job_posting
                          .role_open_reason && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  textTransform: "uppercase",
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  letterSpacing: 0.5,
                                }}
                              >
                                Role Open Reason
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ mt: 0.5, fontWeight: 500 }}
                              >
                                {
                                  selectedReq.interest_context.job_posting
                                    .role_open_reason
                                }
                              </Typography>
                            </Grid>
                          )}

                        {selectedReq.interest_context.job_posting
                          .company_type && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  textTransform: "uppercase",
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  letterSpacing: 0.5,
                                }}
                              >
                                Company Type
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  mt: 0.5,
                                  fontWeight: 500,
                                  textTransform: "capitalize",
                                }}
                              >
                                {Array.isArray(
                                  selectedReq.interest_context.job_posting
                                    .company_type,
                                )
                                  ? selectedReq.interest_context.job_posting.company_type.join(
                                    ", ",
                                  )
                                  : selectedReq.interest_context.job_posting.company_type.replace(
                                    "/",
                                    " / ",
                                  )}
                              </Typography>
                            </Grid>
                          )}

                        {selectedReq.interest_context.job_posting
                          .work_environment &&
                          selectedReq.interest_context.job_posting
                            .work_environment.length > 0 && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  textTransform: "uppercase",
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  letterSpacing: 0.5,
                                }}
                              >
                                Work Environment
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                sx={{ mt: 0.5 }}
                                flexWrap="wrap"
                              >
                                {selectedReq.interest_context.job_posting.work_environment.map(
                                  (env, index) => (
                                    <Chip
                                      key={index}
                                      label={env}
                                      size="small"
                                      color="secondary"
                                      variant="outlined"
                                      sx={{ fontWeight: 500 }}
                                    />
                                  ),
                                )}
                              </Stack>
                            </Grid>
                          )}
                      </Grid>
                    </Box>

                    {/* Why Join Team */}
                    {selectedReq.interest_context.job_posting.why_join_team && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          backgroundColor: "grey.50",
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            mb: 1.5,
                            fontWeight: 600,
                            color: "primary.main",
                          }}
                        >
                          Why Join Our Team
                        </Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                          {
                            selectedReq.interest_context.job_posting
                              .why_join_team
                          }
                        </Typography>
                      </Paper>
                    )}

                    {/* Language Requirements */}
                    {selectedReq.interest_context.job_posting
                      .language_requirements &&
                      selectedReq.interest_context.job_posting
                        .language_requirements.length > 0 && (
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 1.5,
                              fontWeight: 600,
                              color: "primary.main",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <LanguageIcon sx={{ mr: 1, fontSize: 24 }} />
                            Language Requirements
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {selectedReq.interest_context.job_posting.language_requirements.map(
                              (lang, index) => (
                                <Chip
                                  key={index}
                                  label={`${lang.language} (${lang.fluency})`}
                                  size="medium"
                                  // color="info"
                                  variant="outlined"
                                  sx={{
                                    fontWeight: 500,
                                    borderColor: "#ba68c8",
                                    color: "#ba68c8",
                                  }}
                                />
                              ),
                            )}
                          </Stack>
                        </Box>
                      )}

                    {/* Soft Skills */}
                    {selectedReq.soft_skills &&
                      selectedReq.soft_skills.length > 0 && (
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 1.5,
                              fontWeight: 600,
                              color: "primary.main",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <StarIcon sx={{ mr: 1, fontSize: 24 }} />
                            Soft Skills
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {selectedReq.soft_skills.map((skill, index) => (
                              <Chip
                                key={index}
                                label={skill}
                                size="medium"
                                // color="success"
                                variant="outlined"
                                sx={{
                                  fontWeight: 500,
                                  borderColor: "#9b42e9",
                                  color: "#9b42e9",
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}

                    {/* Technical Skills */}
                    {selectedReq.technical_skills && (
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: "primary.main",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <SchoolIcon sx={{ mr: 1, fontSize: 24 }} />
                          Technical Skills
                        </Typography>

                        {/* Must Have Skills */}
                        {selectedReq.technical_skills.must_have &&
                          selectedReq.technical_skills.must_have.length > 0 && (
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                mb: 2,
                                backgroundColor: "rgba(155, 66, 233, 0.1)",
                                color: "#9b42e9",
                                border: "1px solid #9b42e9",
                                borderRadius: 2,
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 600,
                                  mb: 1.5,
                                  color: "inherit",
                                }}
                              >
                                Must Have
                              </Typography>
                              <Stack spacing={1}>
                                {selectedReq.technical_skills.must_have.map(
                                  (skillObj, index) => {
                                    const skillName = Object.keys(skillObj)[0];
                                    const skillData = skillObj[skillName];
                                    return (
                                      <Box
                                        key={index}
                                        sx={{
                                          p: 1.5,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: "medium" }}
                                        >
                                          {skillName}
                                          {skillData.level &&
                                            (!skillData.subSkill ||
                                              skillData.subSkill.length ===
                                              0) && (
                                              <Chip
                                                label={skillData.level}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                  ml: 1,
                                                  height: 20,
                                                  borderColor: "#9b42e9",
                                                  color: "#9b42e9",
                                                }}
                                              />
                                            )}
                                        </Typography>
                                        {skillData.subSkill &&
                                          skillData.subSkill.length > 0 && (
                                            <Stack
                                              direction="row"
                                              spacing={0.5}
                                              sx={{ mt: 1 }}
                                              flexWrap="wrap"
                                            >
                                              {skillData.subSkill.map(
                                                (subSkill, subIndex) => (
                                                  <Chip
                                                    key={subIndex}
                                                    label={
                                                      typeof subSkill ===
                                                        "object"
                                                        ? `${subSkill.name} (${subSkill.level})`
                                                        : subSkill
                                                    }
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                      fontSize: "0.7rem",
                                                      borderColor: "#9b42e9",
                                                      color: "#9b42e9",
                                                    }}
                                                  />
                                                ),
                                              )}
                                            </Stack>
                                          )}
                                      </Box>
                                    );
                                  },
                                )}
                              </Stack>
                            </Paper>
                          )}

                        {/* Should Have Skills */}
                        {selectedReq.technical_skills.should_have &&
                          selectedReq.technical_skills.should_have.length >
                          0 && (
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                mb: 2,
                                backgroundColor: "rgba(186, 104, 200, 0.1)",
                                color: "#ba68c8",
                                border: "1px solid #ba68c8",
                                borderRadius: 2,
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 600,
                                  mb: 1.5,
                                  color: "inherit",
                                }}
                              >
                                Should Have
                              </Typography>
                              <Stack spacing={1}>
                                {selectedReq.technical_skills.should_have.map(
                                  (skillObj, index) => {
                                    const skillName = Object.keys(skillObj)[0];
                                    const skillData = skillObj[skillName];
                                    return (
                                      <Box
                                        key={index}
                                        sx={{
                                          p: 1.5,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: "medium" }}
                                        >
                                          {skillName}
                                          {skillData.level &&
                                            (!skillData.subSkill ||
                                              skillData.subSkill.length ===
                                              0) && (
                                              <Chip
                                                label={skillData.level}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                  ml: 1,
                                                  height: 20,
                                                  borderColor: "#ba68c8",
                                                  color: "#ba68c8",
                                                }}
                                              />
                                            )}
                                        </Typography>
                                        {skillData.subSkill &&
                                          skillData.subSkill.length > 0 && (
                                            <Stack
                                              direction="row"
                                              spacing={0.5}
                                              sx={{ mt: 1 }}
                                              flexWrap="wrap"
                                            >
                                              {skillData.subSkill.map(
                                                (subSkill, subIndex) => (
                                                  <Chip
                                                    key={subIndex}
                                                    label={
                                                      typeof subSkill ===
                                                        "object"
                                                        ? `${subSkill.name} (${subSkill.level})`
                                                        : subSkill
                                                    }
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                      fontSize: "0.7rem",
                                                      borderColor: "#ba68c8",
                                                      color: "#ba68c8",
                                                    }}
                                                  />
                                                ),
                                              )}
                                            </Stack>
                                          )}
                                      </Box>
                                    );
                                  },
                                )}
                              </Stack>
                            </Paper>
                          )}

                        {/* Could Have Skills */}
                        {selectedReq.technical_skills.could_have &&
                          selectedReq.technical_skills.could_have.length >
                          0 && (
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                backgroundColor: "rgba(140, 94, 180, 0.1)",
                                color: "#8c5eb4",
                                border: "1px solid #8c5eb4",
                                borderRadius: 2,
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 600,
                                  mb: 1.5,
                                  color: "inherit",
                                }}
                              >
                                Could Have
                              </Typography>
                              <Stack spacing={1}>
                                {selectedReq.technical_skills.could_have.map(
                                  (skillObj, index) => {
                                    const skillName = Object.keys(skillObj)[0];
                                    const skillData = skillObj[skillName];
                                    return (
                                      <Box
                                        key={index}
                                        sx={{
                                          p: 1.5,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: "medium" }}
                                        >
                                          {skillName}
                                          {skillData.level &&
                                            (!skillData.subSkill ||
                                              skillData.subSkill.length ===
                                              0) && (
                                              <Chip
                                                label={skillData.level}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                  ml: 1,
                                                  height: 20,
                                                  borderColor: "#8c5eb4",
                                                  color: "#8c5eb4",
                                                }}
                                              />
                                            )}
                                        </Typography>
                                        {skillData.subSkill &&
                                          skillData.subSkill.length > 0 && (
                                            <Stack
                                              direction="row"
                                              spacing={0.5}
                                              sx={{ mt: 1 }}
                                              flexWrap="wrap"
                                            >
                                              {skillData.subSkill.map(
                                                (subSkill, subIndex) => (
                                                  <Chip
                                                    key={subIndex}
                                                    label={
                                                      typeof subSkill ===
                                                        "object"
                                                        ? `${subSkill.name} (${subSkill.level})`
                                                        : subSkill
                                                    }
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                      fontSize: "0.7rem",
                                                      borderColor: "#8c5eb4",
                                                      color: "#8c5eb4",
                                                    }}
                                                  />
                                                ),
                                              )}
                                            </Stack>
                                          )}
                                      </Box>
                                    );
                                  },
                                )}
                              </Stack>
                            </Paper>
                          )}
                      </Box>
                    )}

                    {/* ...rest of your existing Job Details, Why Join, Languages, Skills, etc... */}
                  </Stack>
                ) : (
                  // Fallback when there is no job_posting or it's empty
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: "1px dashed",
                      borderColor: "divider",
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      No job details found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      There is currently no job posting information to display.
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}

            {activeTab === 2 &&
              getInterviewStatusDetails(selectedReq) &&
              selectedReq?.interview_stages && (
                <Box>
                  <Stack spacing={3}>
                    {(() => {
                      const interviewStages = selectedReq?.interview_stages;
                      const interviewDetails =
                        getInterviewStatusDetails(selectedReq);
                      console.log(interviewDetails);

                      return (
                        <>
                          {/* Interview Header */}
                          <Box>
                            <Typography
                              variant="h5"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                                fontWeight: 600,
                                color: "primary.main",
                              }}
                            >
                              <EventNoteIcon sx={{ mr: 1.5, fontSize: 32 }} />
                              Interview Status
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 2,
                                mt: 2,
                                flexWrap: "wrap",
                              }}
                            >
                              {interviewDetails?.status && (
                                <Chip
                                  label={`Status: ${interviewDetails.status
                                    .charAt(0)
                                    .toUpperCase() +
                                    interviewDetails.status.slice(1)
                                    }`}
                                  sx={{
                                    fontWeight: 600,
                                    ...getStatusColor(interviewDetails.status),
                                  }}
                                />
                              )}
                              {interviewDetails?.overallResult && (
                                <Chip
                                  // icon={
                                  //   interviewDetails.overallResult ===
                                  //   "selected" ? (
                                  //     <CheckCircleIcon />
                                  //   ) : (
                                  //     <CancelIcon />
                                  //   )
                                  // }
                                  label={`Result: ${interviewDetails.overallResult
                                    .charAt(0)
                                    .toUpperCase() +
                                    interviewDetails.overallResult.slice(1)
                                    }`}
                                  sx={{
                                    fontWeight: 600,
                                    ...getResultColor(
                                      interviewDetails.overallResult,
                                    ),
                                  }}
                                />
                              )}
                              <Chip
                                label="Type: Interest Based"
                                color="primary"
                                variant="outlined"
                                sx={{ fontWeight: 500 }}
                              />
                            </Box>
                          </Box>

                          <Divider />

                          {/* Interview Stages */}
                          {Array.isArray(interviewStages) &&
                            interviewStages.length > 0 ? (
                            <Box>
                              <Typography
                                variant="h6"
                                sx={{
                                  mb: 2,
                                  fontWeight: 600,
                                  color: "primary.main",
                                }}
                              >
                                Interview Stages
                              </Typography>
                              <Stack spacing={2}>
                                {interviewStages.map((stage, index) => (
                                  <Paper
                                    key={index}
                                    elevation={0}
                                    sx={{
                                      p: 2.5,
                                      backgroundColor: "grey.50",
                                      borderRadius: 2,
                                      border: "1px solid",
                                      borderColor: "divider",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        mb: 1.5,
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle1"
                                        sx={{
                                          fontWeight: 600,
                                          color: "primary.main",
                                        }}
                                      >
                                        {stage.stageName}
                                      </Typography>
                                      {/* {interview.stageFlags &&
                                      interview.stageFlags.find(
                                        (flag) =>
                                          flag.stageName === stage.stageName
                                      )?.hasFeedback && (
                                        <Chip
                                          label="Feedback Provided"
                                          size="small"
                                          sx={{
                                            fontWeight: 500,
                                            backgroundColor: "#9b42e9",
                                            color: "#FFFFFF",
                                          }}
                                        />
                                      )} */}
                                    </Box>
                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                      {stage.date && (
                                        <Grid item xs={12} sm={6}>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: "text.secondary",
                                              textTransform: "uppercase",
                                              fontSize: "0.7rem",
                                              fontWeight: 600,
                                              letterSpacing: 0.5,
                                            }}
                                          >
                                            Date
                                          </Typography>
                                          <Typography
                                            variant="body1"
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              mt: 0.5,
                                              fontWeight: 500,
                                            }}
                                          >
                                            <EventNoteIcon
                                              sx={{
                                                mr: 0.5,
                                                fontSize: 18,
                                                color: "primary.main",
                                              }}
                                            />
                                            {new Date(
                                              stage.date,
                                            ).toLocaleDateString()}
                                          </Typography>
                                        </Grid>
                                      )}
                                      {stage.time && (
                                        <Grid item xs={12} sm={6}>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: "text.secondary",
                                              textTransform: "uppercase",
                                              fontSize: "0.7rem",
                                              fontWeight: 600,
                                              letterSpacing: 0.5,
                                            }}
                                          >
                                            Time
                                          </Typography>
                                          <Typography
                                            variant="body1"
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              mt: 0.5,
                                              fontWeight: 500,
                                            }}
                                          >
                                            <AccessTimeIcon
                                              sx={{
                                                mr: 0.5,
                                                fontSize: 18,
                                                color: "primary.main",
                                              }}
                                            />
                                            {stage.time}
                                          </Typography>
                                        </Grid>
                                      )}
                                      {(stage.interviewers?.length > 0 ||
                                        stage.interviewer) && (
                                          <Grid item xs={12} sm={6}>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: "text.secondary",
                                                textTransform: "uppercase",
                                                fontSize: "0.7rem",
                                                fontWeight: 600,
                                                letterSpacing: 0.5,
                                              }}
                                            >
                                              {stage.interviewers?.length > 1
                                                ? "Interviewers"
                                                : "Interviewer"}
                                            </Typography>
                                            <Typography
                                              variant="body1"
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                mt: 0.5,
                                                fontWeight: 500,
                                              }}
                                            >
                                              <PersonIcon
                                                sx={{
                                                  mr: 0.5,
                                                  fontSize: 18,
                                                  color: "primary.main",
                                                }}
                                              />
                                              {stage.interviewers &&
                                                stage.interviewers.length > 0
                                                ? stage.interviewers.join(", ")
                                                : stage.interviewer}
                                            </Typography>
                                          </Grid>
                                        )}
                                    </Grid>
                                    {stage.candidateStageFeedback && (
                                      <Box sx={{ mt: 2 }}>
                                        <Typography
                                          variant="subtitle2"
                                          sx={{
                                            mb: 1,
                                            fontWeight: 600,
                                            color: "primary.main",
                                          }}
                                        >
                                          Feedback
                                        </Typography>
                                        <Paper
                                          elevation={0}
                                          sx={{
                                            p: 2,
                                            backgroundColor: "background.paper",
                                            borderRadius: 1,
                                            border: "1px solid",
                                            borderColor: "divider",
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{ lineHeight: 1.7 }}
                                          >
                                            {stage.candidateStageFeedback}
                                          </Typography>
                                        </Paper>
                                      </Box>
                                    )}
                                  </Paper>
                                ))}
                              </Stack>
                            </Box>
                          ) : (
                            <Box>
                              <Typography
                                variant="h6"
                                sx={{
                                  mb: 2,
                                  fontWeight: 600,
                                  color: "primary.main",
                                }}
                              >
                                Interview Stages
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontStyle: "italic" }}
                              >
                                Not set
                              </Typography>
                            </Box>
                          )}

                          {/* Corporate Notes */}
                          {interviewDetails?.notesFromCorporate && (
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2.5,
                                backgroundColor: "#f3e5f5",
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "#ba68c8",
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  mb: 1.5,
                                  fontWeight: 600,
                                  color: "#ba68c8",
                                }}
                              >
                                Notes from Corporate
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ lineHeight: 1.7 }}
                              >
                                {interviewDetails.notesFromCorporate}
                              </Typography>
                            </Paper>
                          )}

                          {/* Overall Feedback */}
                          {selectedReq?.interview_overall_feedback && (
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2.5,
                                backgroundColor: "#f3e5f5",
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "#ba68c8",
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  mb: 1.5,
                                  fontWeight: 600,
                                  color: "#ba68c8",
                                }}
                              >
                                Overall Feedback
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ lineHeight: 1.7 }}
                              >
                                {selectedReq?.interview_overall_feedback}
                              </Typography>
                            </Paper>
                          )}

                          {/* Candidate Note */}
                          {/* {interview.candidateNote && (
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2.5,
                              backgroundColor: "#f3e5f5",
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: "#ba68c8",
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{
                                mb: 1.5,
                                fontWeight: 600,
                                color: "#ba68c8",
                              }}
                            >
                              Your Note
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ lineHeight: 1.7 }}
                            >
                              {interview.candidateNote}
                            </Typography>
                          </Paper>
                        )} */}

                          {/* Interview Dates */}
                          {/* {interview.createdAt || interview.updatedAt ? (
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                mb: 2,
                                fontWeight: 600,
                                color: "primary.main",
                              }}
                            >
                              Timeline
                            </Typography>
                            <Grid container spacing={2}>
                              {interview.createdAt && (
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      textTransform: "uppercase",
                                      fontSize: "0.7rem",
                                      fontWeight: 600,
                                      letterSpacing: 0.5,
                                    }}
                                  >
                                    Created At
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{ mt: 0.5, fontWeight: 500 }}
                                  >
                                    {new Date(
                                      interview.createdAt
                                    ).toLocaleString()}
                                  </Typography>
                                </Grid>
                              )}
                              {interview.updatedAt && (
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      textTransform: "uppercase",
                                      fontSize: "0.7rem",
                                      fontWeight: 600,
                                      letterSpacing: 0.5,
                                    }}
                                  >
                                    Last Updated
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{ mt: 0.5, fontWeight: 500 }}
                                  >
                                    {new Date(
                                      interview.updatedAt
                                    ).toLocaleString()}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        ) : null} */}
                        </>
                      );
                    })()}
                  </Stack>
                </Box>
              )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ p: 2.5, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Button
            onClick={handleCloseDialog}
            variant="contained"
            color="primary"
            sx={{ minWidth: 100, fontWeight: 600, textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default CorporateInterestRequest;
